
import { supabase } from "@/integrations/supabase/client";
import { siteMetadata } from "@/config/siteMetadata";

// Interface for SEO metadata
export interface SeoMetadata {
  title: string;
  description: string;
  keywords: string[];
}

// Function to format keywords array into a comma-separated string
export const formatKeywordsString = (keywords: string[]): string => {
  // Ensure keywords is an array before joining
  if (!Array.isArray(keywords)) {
    console.warn("Keywords is not an array:", keywords);
    return "";
  }
  return keywords.join(", ");
};

// Map routes to their respective SEO data
export const getSeoDataForRoute = (route: string): SeoMetadata | null => {
  // Default values if no match
  const defaultData = {
    title: siteMetadata.defaultTitle,
    description: siteMetadata.defaultDescription,
    keywords: siteMetadata.defaultKeywords
  };
  
  // Map of routes to SEO data
  const routeSeoMap: Record<string, SeoMetadata> = {
    "/": {
      title: "Lainasampo.fi - Vertaile lainoja ja löydä paras lainatarjous",
      description: "Kilpailutamme lainasi puolestasi yli 20 eri pankissa ja rahoitusyhtiössä. Vertaile lainoja ja hae edullinen lainatarjous ilmaiseksi!",
      keywords: ["lainasampo", "lainavertailu", "edullinen laina", "lainatarjous", "kilpailuta laina", "lainojen vertailu"]
    },
    "/kilpailuta": {
      title: "Kilpailuta lainasi ilmaiseksi | Lainasampo.fi",
      description: "Täytä hakemuksesi ilmaiseksi ja saat useita lainatarjouksia vertailtavaksi. Säästä rahaa ja aikaa lainan kilpailutuksella.",
      keywords: ["lainojen kilpailutus", "ilmainen kilpailutus", "lainatarjoukset", "vertaa lainoja", "säästä lainassa", "kilpailuta"]
    },
    "/lainatuotteet": {
      title: "Lainatuotteet | Lainasampo.fi - Kaikki lainat yhdessä paikassa",
      description: "Selaa eri lainatuotteita ja löydä tarpeisiisi sopiva laina. Asuntolainat, kulutusluotot, yhdistelylainat, autolainat ja muut lainatuotteet.",
      keywords: ["lainatuotteet", "eri lainatyypit", "asuntolaina", "kulutusluotto", "autolaina", "yhdistelylaina"]
    },
    "/kuvapankki": {
      title: "Kuvapankki | Lainasampo.fi - Rahoitusalan laadukkaat kuvat",
      description: "Selaa kattavaa kokoelmaa laadukkaita rahoitusalan kuvia Lainasampo.fi kuvapankissa. Löydä sopivat kuvat lainoihin, asumiseen ja suomalaiseen elämäntyyliin.",
      keywords: ["kuvapankki", "rahoituskuvat", "lainakuvat", "asumisen kuvat", "suomalaiset kuvat", "lainasampo kuvat", "pohjoismainen tyyli", "rahoituskuvitus"]
    },
    "/admin/kuvapankki": {
      title: "Kuvahallinnan ylläpito | Lainasampo.fi",
      description: "Hallinnoi kuvapankin kuvia ja kategorioita Lainasampo.fi-sivustolla.",
      keywords: ["kuvahallinta", "ylläpito", "kuvapankki", "kuvalataus", "kuvatagit", "kuvakategoriat"]
    },
    // Add other routes as needed
  };
  
  return routeSeoMap[route] || defaultData;
};

// Fetch SEO metadata from Supabase
export const fetchSeoMetadata = async (path: string): Promise<SeoMetadata> => {
  try {
    // First try to get data from local mapping
    const localSeoData = getSeoDataForRoute(path);
    if (localSeoData) {
      console.info(`Using local SEO data for path: ${path}`);
      return localSeoData;
    }
    
    // If not found locally, try Supabase
    const { data, error } = await supabase
      .from('seo_keywords')
      .select('title, description, keywords')
      .eq('page_path', path)
      .maybeSingle();
      
    if (error) {
      console.error('Error fetching SEO metadata:', error);
      return getDefaultSeoData();
    }
    
    if (data) {
      return {
        title: data.title || siteMetadata.defaultTitle,
        description: data.description || siteMetadata.defaultDescription,
        keywords: Array.isArray(data.keywords) ? data.keywords : siteMetadata.defaultKeywords
      };
    }
    
    // If no data found for exact path, try to find a match with a trailing slash or without
    const alternativePath = path.endsWith('/') ? path.slice(0, -1) : `${path}/`;
    
    const { data: alternativeData, error: alternativeError } = await supabase
      .from('seo_keywords')
      .select('title, description, keywords')
      .eq('page_path', alternativePath)
      .maybeSingle();
      
    if (!alternativeError && alternativeData) {
      return {
        title: alternativeData.title || siteMetadata.defaultTitle,
        description: alternativeData.description || siteMetadata.defaultDescription,
        keywords: Array.isArray(alternativeData.keywords) ? alternativeData.keywords : siteMetadata.defaultKeywords
      };
    }
    
    // If we couldn't find any data, return default values
    return getDefaultSeoData();
  } catch (error) {
    console.error('Error in fetchSeoMetadata:', error);
    return getDefaultSeoData();
  }
};

// Helper function to get default SEO data
const getDefaultSeoData = (): SeoMetadata => {
  return {
    title: siteMetadata.defaultTitle,
    description: siteMetadata.defaultDescription,
    keywords: siteMetadata.defaultKeywords
  };
};

// Save SEO metadata to Supabase
export const saveSeoMetadata = async (pagePath: string, metadata: SeoMetadata): Promise<boolean> => {
  try {
    // Check if user is authenticated before attempting to save data
    const { data: session } = await supabase.auth.getSession();
    
    if (!session?.session) {
      console.warn('User not authenticated. Cannot save SEO metadata.');
      return false;
    }
    
    const { error } = await supabase
      .from('seo_keywords')
      .upsert({
        page_path: pagePath,
        title: metadata.title,
        description: metadata.description,
        keywords: metadata.keywords
      }, { onConflict: 'page_path' });
    
    if (error) {
      console.error('Error saving SEO metadata:', error);
      return false;
    }
    
    console.info(`SEO metadata saved for: ${pagePath}`);
    return true;
  } catch (error) {
    console.error('Error in saveSeoMetadata:', error);
    return false;
  }
};
